/**
 * Settings (new in M1, styled like the designs' sheets and cards): the account (register, log in
 * or out, download or delete your data, A3), language, sound, vibration, reduced motion, bot
 * difficulty, sharing game stats (when analytics is set up), resetting progress behind a confirm,
 * and the Privacy policy and Terms.
 */
import type { BotDifficulty } from '@caged/shared';
import { useEffect, useId, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isTranslated } from '../../i18n';
import { DEV_GUEST, devGuestId, serverProgress } from '../../net/config';
import { analyticsConfigured, setStatsEnabled, statsEnabled, subscribeStats } from '../../platform/analytics';
import { metaConfigured } from '../../platform/meta';
import type { ProfileRemote } from '../../net/progressApi';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { countryNames } from '../countries';
import { DesktopHeader, TopBar } from '../components/Chrome';
import { Segmented, Slider, Switch } from '../components/Controls';
import { LanguageSheet, languageOf } from '../components/LanguageSheet';
import { Modal } from '../components/Modal';
import { ReminderRows } from '../components/Reminders';
import { Row } from '../components/SettingRow';
import ui from '../components/ui.module.css';
import { useLayoutMode, useScrollTop } from '../hooks';
import '../ui.css';
import s from './SettingsScreen.module.css';

function Card({ title, children, id }: { title: string; children: ReactNode; id: string }) {
  return (
    <section className={[ui.card, s.card].join(' ')} aria-labelledby={id}>
      <h2 id={id} className={[ui.label, s.cardTitle].join(' ')}>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** The league country (one free change, L8) and fight reminders (M5, D56). */
function LeagueCard() {
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState<ProfileRemote | null>(null);
  const [changing, setChanging] = useState(false);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const names = useMemo(() => countryNames(i18n.language), [i18n.language]);
  useEffect(() => {
    void import('../../net/progressApi')
      .then((api) => api.fetchProfileRemote())
      .then(setProfile, () => setProfile(null));
  }, []);
  // The first change is at once; later ones wait for the season's end (L8). Asking for the
  // current country again calls a waiting move off.
  const free = !!profile?.countryChangeLeft;
  const moving = profile?.movingTo ?? null;
  const change = async (to: string) => {
    if (!to || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const api = await import('../../net/progressApi');
      await api.changeCountryRemote(to);
      setProfile(await api.fetchProfileRemote());
      void useLeague.getState().load(true);
      setChanging(false);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t('screens.settings.league')} id="set-league">
      {profile?.country && (
        <Row
          label={t('screens.settings.leagueCountry', { country: names.name(profile.country) })}
          hint={
            moving
              ? t('screens.settings.countryMoving', { country: names.name(moving) })
              : free
                ? t('screens.settings.countryChangeHint')
                : t('screens.settings.countryMoveHint')
          }
        >
          {moving ? (
            <button type="button" className={ui.ghost} onClick={() => void change(profile.country!)} disabled={busy}>
              {t('screens.settings.cancelMove')}
            </button>
          ) : (
            <button
              type="button"
              className={[ui.ghost, s.dangerBtn].join(' ')}
              onClick={() => {
                setPick('');
                setFailed(false);
                setChanging(true);
              }}
            >
              {t('screens.settings.change')}
            </button>
          )}
        </Row>
      )}
      {failed && !changing && (
        <p className={s.rowAlert} role="alert">
          {t('common.saveFailed')}
        </p>
      )}
      <ReminderRows />
      {changing && (
        <Modal onClose={() => setChanging(false)} labelledBy="country-title" variant="sheet">
          <div className={s.dialog}>
            <span className={ui.label}>{t('screens.settings.league')}</span>
            <h2 id="country-title" className={s.dialogTitle}>
              {t('screens.settings.countryTitle')}
            </h2>
            <p className={s.dialogBody}>{t(free ? 'screens.settings.countryBody' : 'screens.settings.countryNextBody')}</p>
            <select className={s.countrySelect} value={pick} onChange={(e) => setPick(e.target.value)} aria-label={t('screens.auth.country')}>
              <option value="" disabled>
                —
              </option>
              {names.sorted.map((c) => (
                <option key={c} value={c}>
                  {names.name(c)}
                </option>
              ))}
            </select>
            {failed && (
              <p className={s.dialogBody} role="alert">
                {t('common.saveFailed')}
              </p>
            )}
            <div className={s.dialogButtons}>
              <button type="button" className={ui.ghost} onClick={() => setChanging(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className={ui.primary} onClick={() => void change(pick)} disabled={!pick || busy || pick === profile?.country}>
                {t(free ? 'screens.settings.countryConfirm' : 'screens.settings.countryConfirmNext')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

export function SettingsScreen() {
  const { t } = useTranslation();
  const layout = useLayoutMode();
  const settings = useApp((st) => st.settings);
  const updateSettings = useApp((st) => st.updateSettings);
  const resetProgress = useApp((st) => st.resetProgress);
  const go = useApp((st) => st.go);
  const [langOpen, setLangOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetFailed, setResetFailed] = useState(false);
  const ids = { sfx: useId(), music: useId(), mute: useId(), vib: useId(), motion: useId(), stats: useId(), reset: useId() };
  const stats = useSyncExternalStore(subscribeStats, statsEnabled);
  const account = useApp((st) => st.account);
  const registered = (!!account?.signedIn && !account.guest) || (DEV_GUEST && devGuestId().startsWith('reg-'));
  const openAuth = useApp((st) => st.openAuth);
  const logOut = useApp((st) => st.logOut);
  useScrollTop();

  const lang = languageOf(settings.language);
  const fallback = !isTranslated(lang.code);

  const who = !account
    ? ''
    : account.guest
      ? t('screens.settings.guest')
      : account.email
        ? t('screens.settings.signedIn', { email: account.email })
        : t('screens.settings.signedInWith', { provider: account.provider === 'facebook' ? 'Facebook' : 'Google' });

  // Accounts (Milestone 4): register or log in as a guest, log out when signed in. A copy of their
  // data, or deleting the account, is asked for by email (info@caged.gg, Privacy policy, D57).
  const accountCard =
    serverProgress() && account ? (
      <Card title={t('screens.settings.account')} id="set-account">
        <div className={s.danger}>
          <div className={s.rowText}>
            <span className={s.rowLabel}>{who}</span>
            <span className={s.rowHint}>{account.guest ? t('screens.settings.guestHint') : t('screens.settings.logoutHint')}</span>
          </div>
          {account.guest ? (
            <div className={s.accountButtons}>
              <button type="button" className={[ui.ghost, s.dangerBtn].join(' ')} onClick={() => openAuth('login')}>
                {t('screens.auth.login')}
              </button>
              <button type="button" className={[ui.primary, s.dangerBtn].join(' ')} onClick={() => openAuth('register')}>
                {t('screens.auth.register')}
              </button>
            </div>
          ) : (
            <button type="button" className={[ui.ghost, s.dangerBtn].join(' ')} onClick={() => void logOut()}>
              {t('screens.settings.logout')}
            </button>
          )}
        </div>
      </Card>
    ) : null;

  const content = (
    <div className={s.stack}>
      {accountCard}
      <Card title={t('screens.settings.language')} id="set-language">
        <button type="button" className={s.langRow} onClick={() => setLangOpen(true)}>
          <span className={s.langNames}>
            <span className={s.langNative} lang={lang.code}>
              {lang.native}
            </span>
            <span className={s.langEnglish}>
              {lang.english}
              {fallback && <span className={s.fallback}> · {t('screens.language.englishForNow')}</span>}
            </span>
          </span>
          <span className={s.langChange}>
            {t('screens.settings.change')}
            <span aria-hidden="true">→</span>
          </span>
        </button>
      </Card>

      {registered && <LeagueCard />}

      <Card title={t('screens.settings.sound')} id="set-sound">
        <Row label={t('screens.settings.sfx')} htmlFor={ids.sfx}>
          <Slider id={ids.sfx} value={settings.sfxVolume} onChange={(v) => updateSettings({ sfxVolume: v })} label={t('screens.settings.sfx')} disabled={settings.muted} />
        </Row>
        <Row label={t('screens.settings.music')} htmlFor={ids.music}>
          <Slider id={ids.music} value={settings.musicVolume} onChange={(v) => updateSettings({ musicVolume: v })} label={t('screens.settings.music')} disabled={settings.muted} />
        </Row>
        <Row label={t('screens.settings.mute')} hint={t('screens.settings.muteHint')}>
          <Switch id={ids.mute} checked={settings.muted} onChange={(v) => updateSettings({ muted: v })} label={t('screens.settings.mute')} />
        </Row>
      </Card>

      <Card title={t('screens.settings.game')} id="set-game">
        <Row label={t('screens.home.difficulty')} hint={t('screens.settings.difficultyHint')}>
          <Segmented<BotDifficulty>
            size="sm"
            label={t('screens.home.difficulty')}
            value={settings.difficulty === 'easy' ? 'easy' : 'normal'}
            options={[
              { value: 'easy', label: t('screens.difficulty.easy') },
              { value: 'normal', label: t('screens.difficulty.normal') },
            ]}
            onChange={(v) => updateSettings({ difficulty: v })}
          />
        </Row>
        <Row label={t('screens.settings.vibration')} hint={t('screens.settings.vibrationHint')}>
          <Switch id={ids.vib} checked={settings.vibration} onChange={(v) => updateSettings({ vibration: v })} label={t('screens.settings.vibration')} />
        </Row>
        <Row label={t('screens.settings.motion')} hint={t('screens.settings.motionHint')}>
          <Switch id={ids.motion} checked={settings.reducedMotion} onChange={(v) => updateSettings({ reducedMotion: v })} label={t('screens.settings.motion')} />
        </Row>
        {(analyticsConfigured || metaConfigured) && (
          <Row label={t('screens.settings.stats')} hint={t('screens.settings.statsHint')}>
            <Switch
              id={ids.stats}
              checked={stats}
              onChange={(v) => void setStatsEnabled(v)}
              label={t('screens.settings.stats')}
            />
          </Row>
        )}
      </Card>

      <Card title={t('screens.settings.progress')} id="set-progress">
        <div className={s.danger}>
          <div className={s.rowText}>
            <span className={s.rowLabel}>{t('screens.settings.resetTitle')}</span>
            <span className={s.rowHint}>{resetDone ? t('screens.settings.resetDone') : t('screens.settings.resetHint')}</span>
          </div>
          <button type="button" className={[ui.danger, s.dangerBtn].join(' ')} onClick={() => setResetOpen(true)}>
            {t('screens.settings.resetButton')}
          </button>
        </div>
      </Card>

      <nav className={s.legal} aria-label={t('screens.settings.legal')}>
        <button type="button" className={s.legalBtn} onClick={() => go('faq')}>
          {t('faq.link')}
        </button>
        <span aria-hidden="true">·</span>
        <a href="/privacy" target="_blank" rel="noopener">
          {t('screens.settings.privacy')}
        </a>
        <span aria-hidden="true">·</span>
        <a href="/terms" target="_blank" rel="noopener">
          {t('screens.settings.terms')}
        </a>
      </nav>
    </div>
  );

  const dialogs = (
    <>
      {langOpen && <LanguageSheet onClose={() => setLangOpen(false)} />}
      {resetOpen && (
        <Modal onClose={() => setResetOpen(false)} labelledBy={ids.reset} variant="sheet">
          <div className={s.dialog}>
            <span className={ui.label}>{t('screens.settings.progress')}</span>
            <h2 id={ids.reset} className={s.dialogTitle}>
              {t('screens.settings.resetConfirmTitle')}
            </h2>
            <p className={s.dialogBody}>{t('screens.settings.resetConfirmBody')}</p>
            {resetFailed && (
              <p className={s.dialogBody} role="alert">
                {t('common.saveFailed')}
              </p>
            )}
            <div className={s.dialogButtons}>
              <button type="button" className={ui.ghost} onClick={() => setResetOpen(false)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className={ui.danger}
                disabled={resetBusy}
                onClick={() => {
                  setResetBusy(true);
                  void resetProgress().then((ok) => {
                    setResetBusy(false);
                    setResetFailed(!ok);
                    if (!ok) return;
                    setResetDone(true);
                    setResetOpen(false);
                  });
                }}
              >
                {t('screens.settings.resetConfirmButton')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  if (layout === 'desktop') {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <main className={s.deskMain}>
          <div className={s.deskTitleBlock}>
            <span className={ui.label}>{t('screens.nav.settings')}</span>
            <h1 className={s.deskTitle}>{t('screens.settings.title')}</h1>
          </div>
          {content}
        </main>
        {dialogs}
      </div>
    );
  }

  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={ui.column}>
        <TopBar title={t('screens.nav.settings')} />
        {content}
      </div>
      {dialogs}
    </div>
  );
}
